import React from 'react';
import { Percent, Gift, Package, Copy, ChevronRight, Wallet } from 'lucide-react';
import { Voucher } from '../types';
import toast from 'react-hot-toast';
import { sanitizeString } from '../utils/sanitization';
import { formatCurrencyNPR } from '../utils/currency';

interface CouponCardProps {
  voucher: Voucher;
  isClaimed?: boolean;
  onClaim?: (voucherId: string) => void;
  compact?: boolean;
  isClaiming?: boolean;
  onSelect?: (voucher: Voucher) => void; // For checkout selection
}

const CouponCard: React.FC<CouponCardProps> = ({
  voucher,
  isClaimed = false,
  onClaim,
  compact = false,
  isClaiming = false,
  onSelect,
}) => {
  // Format discount value
  const formatDiscount = () => {
    if (voucher.discountType === 'percentage') {
      return `${voucher.discountValue}% OFF`;
    } else if (voucher.discountType === 'free_delivery') {
      return 'FREE DELIVERY';
    } else {
      return `${formatCurrencyNPR(voucher.discountValue)} OFF`;
    }
  };

  // Get discount icon
  const getDiscountIcon = () => {
    if (voucher.discountType === 'percentage') {
      return <Percent className="w-5 h-5 sm:w-6 sm:h-6" />;
    } else if (voucher.discountType === 'free_delivery') {
      return <Package className="w-5 h-5 sm:w-6 sm:h-6" />;
    } else {
      return <Gift className="w-5 h-5 sm:w-6 sm:h-6" />;
    }
  };

  // Get icon background color
  const iconBgColor = isClaimed ? 'bg-green-500' : 'bg-[#da2516]';

  // Helper function to convert date/timestamp to Date object
  const convertToDate = (dateValue: any): Date | null => {
    if (!dateValue) return null;
    
    if (typeof dateValue.toDate === 'function') {
      return dateValue.toDate();
    } else if (dateValue instanceof Date) {
      return dateValue;
    } else if (dateValue.seconds) {
      return new Date(dateValue.seconds * 1000);
    }
    return null;
  };

  // Format expiry date
  const formatExpiryDate = (): string | null => {
    const date = convertToDate(voucher.expiresAt) || convertToDate(voucher.endDate);
    if (!date) return null;
    
    return date.toLocaleDateString('en-US', { 
      month: 'long', 
      year: 'numeric' 
    });
  };

  // Copy coupon code to clipboard
  const handleCopyCode = (e: React.MouseEvent) => {
    e.stopPropagation();
    const sanitizedCode = sanitizeString(voucher.code, 50);
    if (sanitizedCode) {
      navigator.clipboard.writeText(sanitizedCode);
      toast.success('Coupon code copied!');
    } else {
      toast.error('Invalid coupon code');
    }
  };

  // Handle claim button click
  const handleClaim = (e: React.MouseEvent) => {
    e.stopPropagation();
    if (onClaim && !isClaimed && !isClaiming) {
      onClaim(voucher.id);
    }
  };

  // Handle card click (for checkout selection)
  const handleCardClick = () => {
    if (onSelect && !isClaimed) {
      onSelect(voucher);
    }
  };

  const cardWidth = compact ? 'min-w-[280px] sm:min-w-[300px]' : 'min-w-[320px] sm:min-w-[340px]';
  const expiryDate = formatExpiryDate();

  return (
    <div className={`${cardWidth} flex-shrink-0 snap-start`}>
      {/* New Coupon Design - Fixed Height Card */}
      <div 
        className={`relative bg-white rounded-xl border-2 ${
          isClaimed ? 'border-green-300' : 'border-pink-200'
        } shadow-sm hover:shadow-md ${onSelect ? 'cursor-pointer' : ''} flex flex-col h-full`}
        onClick={handleCardClick}
      >
        {/* Main Content - Fixed height section */}
        <div className="flex items-center gap-3 p-4 flex-1">
          {/* Left: Icon Square */}
          <div className={`flex-shrink-0 w-14 h-14 rounded-lg ${iconBgColor} flex items-center justify-center`}>
            <div className="text-white">
              {getDiscountIcon()}
            </div>
          </div>

          {/* Middle: Discount and Tags */}
          <div className="flex-1 min-w-0">
            {/* Discount Text */}
            <div className={`text-lg font-poppins font-bold mb-2 ${
              isClaimed ? 'text-green-600' : 'text-gray-900'
            }`}>
              {formatDiscount()}
            </div>

            {/* Tags Row */}
            <div className="flex flex-wrap items-center gap-1.5 mb-1">
              {/* Minimum Purchase Tag */}
              {voucher.minPurchaseAmount && voucher.minPurchaseAmount > 0 && (
                <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-md text-xs font-poppins font-medium bg-gray-100 text-gray-700">
                  <Wallet className="w-3 h-3" />
                  Min {formatCurrencyNPR(voucher.minPurchaseAmount)}
                </span>
              )}
            </div>

            {/* Validity Date */}
            {expiryDate && (
              <div className="text-xs font-poppins text-gray-600 mt-0.5">
                Valid until {expiryDate}
              </div>
            )}
          </div>

          {/* Right: Action Button */}
          <div className="flex-shrink-0">
            {isClaimed ? (
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  handleCopyCode(e);
                }}
                className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
                title="Copy code"
              >
                <Copy className="w-5 h-5 text-green-600" />
              </button>
            ) : onSelect ? (
              <ChevronRight className="w-6 h-6 text-gray-400" />
            ) : (
              <button
                onClick={handleClaim}
                disabled={isClaiming || !onClaim}
                className="px-4 py-2 bg-[#da2516] text-white rounded-lg hover:bg-[#b91c1c] text-sm font-poppins font-semibold disabled:opacity-50 disabled:cursor-not-allowed whitespace-nowrap"
              >
                {isClaiming ? 'Claiming...' : 'Claim'}
              </button>
            )}
          </div>
        </div>

        {/* Code Section - Always shown for consistent height */}
        <div className="px-4 pb-3 border-t border-gray-100 pt-2.5">
          <div className="flex items-center justify-between">
            <span className="text-xs font-poppins text-gray-600">Code:</span>
            <span className="text-sm font-poppins font-bold text-gray-900 font-mono">
              {voucher.code}
            </span>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CouponCard;
